/**
 * MyClaimsList — renders the current member's promotion claims with a
 * turnover progress ring and an expiry countdown.
 *
 * Nothing here decides what a status means. Tone and sign come from
 * features/promo/claim-status-display, whose maps are keyed on the shared status
 * union, so a status svc-promo starts writing is a compile error rather than a
 * row that falls through to a default.
 */
import { useTranslation } from 'react-i18next';
import { formatDurationShort } from '@/lib/datetime/duration.js';
import { Card } from '@/components/ui/card.js';
import { Badge } from '@/components/ui/badge.js';
import { Money } from '@/components/money/Money.js';
import { ProgressRing } from './ProgressRing.js';
import {
  bigIntish,
  turnoverProgress,
  type PromotionClaim,
} from '@/features/promo/api.js';
import {
  claimTone,
  claimAmountIsCredited,
  claimAmountIsWithheld,
} from '@/features/promo/claim-status-display.js';

interface MyClaimsListProps {
  claims: PromotionClaim[];
  currency: string;
  isLoading?: boolean;
}

/** Localized expiry duration ("3 วัน 4 ชม.") + expired flag for inline labels. */
function formatExpiry(
  expiresAt: string | null | undefined,
  t: (k: string) => string,
): { text: string; expired: boolean } | null {
  if (!expiresAt) return null;
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (Number.isNaN(ms)) return null;
  if (ms <= 0) return { text: '', expired: true };
  return { text: formatDurationShort(Math.floor(ms / 1000), t), expired: false };
}

/**
 * The amount, signed and coloured by what the STATUS says happened to it.
 *
 * `grantedAmount` survives a forfeit, a rejection and an admin clawback
 * unchanged, so it is not evidence the player kept anything. Reading it alone
 * painted a revoked bonus as `+ ฿50` in success green — identical to money still
 * in the pool — on the history screen a player opens to find out where their
 * bonus went.
 */
function ClaimedAmount({ claim, currency }: { claim: PromotionClaim; currency: string }) {
  const amount = claim.grantedAmount ?? claim.amount;
  if (amount == null) return null;
  const minor = bigIntish(amount);
  const credited = claimAmountIsCredited(claim.status);
  return (
    <span
      className={[
        'font-bold text-sm bf-tnum',
        credited ? 'text-[var(--success)]' : 'text-[var(--text-muted)]',
        claimAmountIsWithheld(claim.status) ? 'line-through' : '',
      ].join(' ')}
    >
      {credited ? '+ ' : ''}
      <Money minor={minor} currency={claim.currency ?? currency} />
    </span>
  );
}

export function MyClaimsList({ claims, currency, isLoading }: MyClaimsListProps) {
  const { t } = useTranslation('promo');
  const { t: tc } = useTranslation('common');

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="h-20 rounded-xl bg-[var(--bg-elevated)]/50 animate-pulse" />
        ))}
      </div>
    );
  }

  if (!claims.length) {
    return (
      <div className="rounded-xl bg-[var(--bg-raised)] py-10 text-center text-[var(--text-muted)] text-[13px]">
        {t('claims.empty')}
      </div>
    );
  }

  return (
    <Card className="overflow-hidden">
      {claims.map((c, i) => {
        const required = bigIntish(c.turnoverRequired);
        const showProgress = required > 0n;
        const progress = showProgress ? turnoverProgress(c) : 0;
        const expiry = formatExpiry(c.expiresAt, tc);
        const status = c.status || 'pending';

        return (
          <div
            key={c.id}
            className={[
              'grid grid-cols-[auto_1fr_auto] items-center px-4 py-3 gap-3',
              i > 0 ? 'border-t border-[var(--border-subtle)]' : '',
            ].join(' ')}
          >
            {/* Progress ring (or status dot when no turnover required) */}
            {showProgress ? (
              <ProgressRing
                progress={progress}
                size={52}
                color={progress >= 1 ? 'var(--success)' : 'var(--primary)'}
              />
            ) : (
              <div className="w-[52px] h-[52px] flex items-center justify-center">
                <div
                  className={[
                    'w-2 h-2 rounded-full',
                    claimAmountIsWithheld(status)
                      ? 'bg-[var(--danger)]'
                      : status === 'completed' || status === 'released'
                        ? 'bg-[var(--success)]'
                        : 'bg-[var(--info)]',
                  ].join(' ')}
                />
              </div>
            )}

            {/* Title + meta */}
            <div className="min-w-0">
              <div className="text-[13px] font-semibold text-[var(--text-primary)] truncate">
                {c.promotionTitle ?? (c.promotionId ? c.promotionId.slice(0, 8) : t('claims.adminBonus'))}
              </div>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                <Badge tone={claimTone(status)} className="text-[10px]">
                  {t(`claims.status.${status}`, { defaultValue: status })}
                </Badge>
                {showProgress && (
                  <span className="text-[11px] font-mono text-[var(--text-muted)]">
                    <Money
                      minor={bigIntish(c.turnoverAchieved)}
                      currency={c.currency ?? currency}
                    />
                    {' / '}
                    <Money minor={required} currency={c.currency ?? currency} />
                  </span>
                )}
                {expiry && (
                  <span
                    className={[
                      'text-[11px] font-mono',
                      expiry.expired ? 'text-[var(--danger)]' : 'text-[var(--text-muted)]',
                    ].join(' ')}
                  >
                    {expiry.expired
                      ? t('claims.expired')
                      : t('claims.expiresIn', { time: expiry.text })}
                  </span>
                )}
              </div>
              {showProgress && progress < 1 && (status === 'active' || status === 'approved') && (
                <div className="text-[11px] text-[var(--text-muted)] mt-1">
                  {t('claims.turnoverHowto')}
                </div>
              )}
            </div>

            {/* Granted amount */}
            <ClaimedAmount claim={c} currency={currency} />
          </div>
        );
      })}
    </Card>
  );
}
